/**
 * Where every container sits on the bench, in profile units (the 3D scene's
 * units; the 2D scene multiplies by PX_SCALE). One module so the two views
 * cannot disagree about how tall a stand is or which vessel is stacked on
 * which — and so the page can ask the same question the scenes do ("can a
 * burner physically reach this vessel?") when it builds the context menu.
 *
 * Shared stands are the reason this exists at all. Two NEIGHBOURING containers
 * both set to 'multi-level' share one rod, stacked; up to four neighbouring
 * test tubes share one rack, side by side in its slots. Both occupy a single
 * bench column. Everything else takes a column of its own.
 */
import { VESSEL_PROFILES } from './vesselGeometry';
import type { VesselType } from './vesselTypes';
import { STAND_CAPACITY, type StandType } from './standTypes';

/** Height of a tripod's gauze above the bench. Tall enough for a burner to
 *  stand underneath with a full flame in the clear air above it. The vessel is
 *  ON the tripod from the moment it goes in one, lit or not, so the burner is
 *  never hidden behind the glassware and the flame never has to overlap it. */
export const TRIPOD_RISE = 0.58;
/** How far a round-bottomed vessel nestles down INTO the gauze ring, as a
 *  fraction of its own radius. A ring cradle holds a sphere below its equator;
 *  a flat base just rests on top. */
export const CRADLE_SINK = 0.3;
/** Thickness of a test-tube rack's tray — the tube's floor rests on it. */
export const RACK_RISE = 0.06;
/** Clamp height of a single vessel on a retort stand: high enough that a
 *  burner fits underneath, which is the whole point of clamping it. */
export const RETORT_RISE = 0.58;
/** The lowest clamp on a multi-level rod. */
export const MULTI_BASE_RISE = 0.58;
/** Clearance between two vessels stacked on one rod. */
export const MULTI_GAP = 0.24;
/** How far clear of the widest glassware the upright rod stands. */
export const ROD_CLEARANCE = 0.26;
/** Rod overshoot above the topmost vessel's rim. */
export const ROD_HEADROOM = 0.2;
/** A test-tube rack has four slots, always — it is drawn full-width whether
 *  one tube or four are standing in it, the way a real rack is. */
export const RACK_SLOTS = STAND_CAPACITY['tube-rack'];
/** Centre-to-centre spacing of those slots. A tube is 0.23 wide, so this
 *  leaves a real gap between neighbours rather than standing them shoulder to
 *  shoulder — two tubes touching read as one object. */
export const RACK_SLOT_PITCH = 0.40;

export interface BenchItem {
  vesselType: VesselType;
  standType: StandType;
}

export interface BenchPlacement {
  /** Bench column this container occupies. Two stacked containers share one. */
  column: number;
  /** 0 for the vessel standing lowest in its column. */
  level: number;
  /** Sideways offset from the column centre — non-zero only for a tube
   *  standing in one of a rack's slots. */
  offsetX: number;
  /** True for the one container in the column that draws the shared stand
   *  furniture (base + rod); the others draw only their own clamp. */
  isColumnBase: boolean;
  /** Height of the vessel's floor above the bench. */
  rise: number;
  /** Sideways offset of the upright rod from the column centre. 0 = no rod. */
  rodX: number;
  /** Rod height above the bench. 0 = no rod. */
  rodTop: number;
  /** Whether a burner standing on the bench could actually reach this vessel.
   *  False for a wooden tube rack (no flame near it) and for anything clamped
   *  above another vessel — the flame is nowhere near it. */
  canHeat: boolean;
}

export interface BenchLayout {
  /** Container indices per column, lowest-first. */
  columns: number[][];
  /** By container index. */
  placements: BenchPlacement[];
}

function riseFor(item: BenchItem): number {
  const profile = VESSEL_PROFILES[item.vesselType];
  switch (item.standType) {
    case 'tripod':
      // A flat base rests on top of the gauze; a round bottom sinks into the
      // ring, the way a ring cradle actually holds one.
      return profile.hasFlatBase
        ? TRIPOD_RISE
        : TRIPOD_RISE - profile.standRadius * CRADLE_SINK;
    case 'tube-rack': return RACK_RISE;
    case 'retort': return RETORT_RISE;
    case 'multi-level': return MULTI_BASE_RISE;
    case 'none': return 0;
  }
}

function hasRod(stand: StandType): boolean {
  return stand === 'retort' || stand === 'multi-level';
}

/** How many neighbours starting at `i` share one stand. */
function runLength(items: BenchItem[], i: number): number {
  const max = STAND_CAPACITY[items[i].standType];
  let n = 1;
  while (n < max && items[i + n]?.standType === items[i].standType) n++;
  return n;
}

export function benchLayout(items: BenchItem[]): BenchLayout {
  const columns: number[][] = [];
  for (let i = 0; i < items.length;) {
    const n = runLength(items, i);
    columns.push(Array.from({ length: n }, (_, k) => i + k));
    i += n;
  }

  const placements: BenchPlacement[] = new Array(items.length);
  columns.forEach((members, column) => {
    const rodX = members.some(i => hasRod(items[i].standType))
      ? Math.max(...members.map(i => VESSEL_PROFILES[items[i].vesselType].maxRadius)) + ROD_CLEARANCE
      : 0;

    const rises: number[] = [];
    members.forEach((i, level) => {
      if (level === 0) rises.push(riseFor(items[i]));
      else {
        const below = items[members[level - 1]];
        rises.push(rises[level - 1] + VESSEL_PROFILES[below.vesselType].height + MULTI_GAP);
      }
    });

    const topIndex = members[members.length - 1];
    const rodTop = rodX === 0
      ? 0
      : rises[members.length - 1] + VESSEL_PROFILES[items[topIndex].vesselType].height + ROD_HEADROOM;

    // A rack's tubes stand side by side in its slots, all at the same height;
    // everything else in a shared column stacks.
    const inRack = items[members[0]].standType === 'tube-rack';
    members.forEach((i, slot) => {
      placements[i] = {
        column,
        level: inRack ? 0 : slot,
        offsetX: inRack ? (slot - (RACK_SLOTS - 1) / 2) * RACK_SLOT_PITCH : 0,
        isColumnBase: slot === 0,
        rise: inRack ? RACK_RISE : rises[slot],
        rodX,
        rodTop,
        canHeat: !inRack && slot === 0,
      };
    });
  });

  return { columns, placements };
}
